import { memo, useCallback } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  SxProps,
  Typography,
} from "@mui/material";

import { useStory } from "../context";

import * as object from "@/utils/object";

const styles: Record<string, SxProps> = {
  root: {
    borderBottom: "1px solid",
    borderColor: "divider",
    "&::before": { display: "none" },
  },

  summary: {
    transition: "background-color 150ms",
    "&:hover": { bgcolor: "background.default" },
  },

  title: {
    textTransform: "uppercase",
    fontWeight: 500,
    letterSpacing: 0.2,
  },

  details: {
    padding: "16px 0 !important",
    bgcolor: "background.default",
    borderTop: "1px solid ",
    borderColor: "divider",
    "& > *": { display: "flex", flexDirection: "column", pb: 1 },

    // Use lobotomized owl selector instead of `gap` to ensure a smooth hover effect.
    "& > * > * + *": { paddingTop: 3 },
  },
};

export interface EditableContainerProps {
  children: React.ReactNode;
  path: object.Path;
  title: string;
  variant: string;
}

const EditableContainer: React.FC<EditableContainerProps> = ({
  children,
  title,
  variant,
}) => {
  const { openContainer, setOpenContainer } = useStory();

  const handleToggle = useCallback(() => {
    setOpenContainer((prev) => (prev === variant ? null : variant));
  }, [variant, setOpenContainer]);

  return (
    <Accordion
      disableGutters
      square
      elevation={0}
      expanded={openContainer === variant}
      sx={styles.root}
      onChange={handleToggle}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.summary}>
        <Typography sx={styles.title} variant="subtitle2">
          {title}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={styles.details}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default memo(EditableContainer);
